import { createPinia } from "pinia"
import { createApp } from "vue"
import App from "./App.vue"
import { setMoneyTestSource } from "./lib/api"
import { captureAttribution } from "./lib/attribution"
import { moneyHeader } from "./lib/experiment"
import { captureKlaviyoKx } from "./lib/klaviyokx"
import { initDevStore } from "./lib/devstore"
import { handlePayPopupReturn } from "./lib/paypopup"
import { initSentry } from "./lib/sentry"
import { initTracking } from "./lib/tracking"
import { router } from "./router"
import { useBootstrapStore } from "./stores/bootstrap"
import "./styles.css"

/** How long a cold visit's mount waits for the bootstrap payload: the same bound
 *  the router's first navigation already waits (router.ts). */
const MOUNT_WAIT_MS = 2000

async function boot() {
  // Snapshot last-touch UTM from the landing URL before anything else runs.
  captureAttribution()
  // Klaviyo's email link token. Must be read HERE, on the landing URL — by the
  // time /checkout assembles its tracking payload the param is long gone.
  captureKlaviyoKx()
  // Dev-only: capture ?preview_store so API calls resolve the previewed store.
  initDevStore()

  const app = createApp(App)
  const pinia = createPinia()
  app.use(pinia)
  // Cart calls say which money A/B tests this page displayed (lib/experiment).
  const bootstrap = useBootstrapStore(pinia)
  setMoneyTestSource(() => moneyHeader(bootstrap.payload?.experiments))
  app.use(router)
  // An SPA route change ends one page and begins the next. Dispatched here, from
  // the one place that owns the router, so lib/interaction can reset its scroll
  // quartiles and close off a dwell without importing the router — and so the
  // entry bundle carries one dispatch rather than a capture module.
  //
  // Carries no data at all: the listener reads location itself. Registered before
  // the mount below waits, so the first navigation is never missed.
  router.afterEach(() => dispatchEvent(new Event("dk:route")))
  // A visitor with no cached payload mounts once it lands, not before. Mounted
  // without it, the app drew its header and announcement bar from empty settings
  // and then redrew them: a 36px bar arriving, or an overlay header leaving the
  // flow, moved the whole page on every first visit, which is what an ad click is.
  // Until then index.html's shell stays up, and theme.css has already sized it
  // for this store. Nothing is lost: the router's first navigation waits for the
  // same payload, and the request is index.html's preload, already in flight.
  if (!bootstrap.hasPayload) {
    await Promise.race([bootstrap.load(), new Promise((done) => setTimeout(done, MOUNT_WAIT_MS))])
  }
  app.mount("#app")

  // Error tracking: lazy, post-mount, no-op unless VITE_SENTRY_DSN is set.
  // Loads in its own async chunk so the entry bundle / first paint are unaffected.
  initSentry(app)

  // Marketing pixels: SPA page views + the commerce funnel. The tracker itself is a
  // lazy chunk pulled at idle on the first event; no-op when the store configured none.
  initTracking(router)

  // Prefetch products on hover/press so the router's navigation hold usually costs
  // nothing. Dynamically imported at idle so the module never touches the entry
  // chunk (the 70KB gz budget), same handoff as initSentry/initTracking above.
  const idle = (cb: () => void, timeout?: number) =>
    "requestIdleCallback" in window
      ? window.requestIdleCallback(cb, timeout ? { timeout } : undefined)
      : setTimeout(cb, 200)
  idle(() => void import("./lib/prefetch").then(m => m.installLinkPrefetch()))

  // Record this visit. NOT gated on the cookie banner — see lib/sessionping.ts for
  // where that line is drawn and why. At idle so it never competes with LCP, but
  // with a 2s timeout guarantee rather than the open-ended wait prefetch is happy
  // with: a shopper who bounces in three seconds is exactly the visit a traffic
  // report must not silently drop, and requestIdleCallback alone may never fire on
  // a page that stays busy.
  idle(() => void import("./lib/sessionping").then(m => m.pingSession()), 2000)
}

// This window may be the PayPal approval popup coming back. If so it reports to the
// checkout that opened it and closes — before anything else, so it never boots the
// storefront, fetches a bootstrap, or reports a page view for a window nobody sees.
if (!handlePayPopupReturn()) {
  void boot()
}
